/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useInfiniteQuery, useQueryClient} from 'react-query';
import {loadGeofences} from '@ion/core/src/services/ArcGIS';
import {GenericObject} from '@ion/core/src/types/entities';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, getLastTime} from '../utils/helpers';
import {
  getLoadedCount,
  removeTrailingEmptyPage,
  UseLoadGeofence,
  UseLoadGeofenceResults
} from './useLoadGeofences';

export type UseLoadGeofenceUpdates = UseLoadGeofence & {
  parentBaseKey: QueryKey;
  propertyTime?: string;
  refetchInterval?: number;
};

const getFilter = (property: string, time: number) => {
  return `${property} > '${new Date(time).toISOString()}'`;
};

/**
 * The useLoadGeofenceUpdates hook provides the Query for referencing the latest geofence data.
 *
 * ```typescript
 *
 * import {useLoadGeofenceUpdates} from '@ion/query/src/hooks/useLoadGeofenceUpdates';
 *
 *  ...
 *
 *  const {data: geofences} = useLoadGeofenceUpdates();
 *
 * ```
 * @param   parentBaseKey a constant passed in to identify the dependency parent key.
 * @param   baseKey a constant passed in to identify the dependency key.
 * @param   enabled when true, the query will be executed immediately. Defaults to
 *          false in order to allow for updates to occur only at the user's discretion.
 * @param   queryParams is built through Report filters to be used in the CVS request.
 * @param   globalGroupId used in the CVS request.
 * @returns UseAssetResult containing the query results and the current Tracking Asset.
 */
export const useLoadGeofenceUpdates = ({
  baseKey,
  queryParams,
  parentBaseKey,
  globalGroupId,
  propertyTime = 'modifiedDate',
  rowsPerPage = 10000,
  refetchInterval = 20000,
  enabled = false
}: UseLoadGeofenceUpdates): UseLoadGeofenceResults => {
  const lastTimeRef             = useRef(null);
  const sinceTimeRef            = useRef(null);
  const [lastTime, setLastTime] = useState(null);
  const queryClient             = useQueryClient();

  const parentkeys = useMemo(() => (
    createKey(parentBaseKey, globalGroupId)
  ), [parentBaseKey, globalGroupId]);
    
  const cachedData = queryClient.getQueryData(parentkeys) as GenericObject;

  useEffect(() => {
    setLastTime(null);
  }, [cachedData, queryParams]);

  useEffect(() => {
    if (enabled) {
      lastTimeRef.current = lastTime || getLastTime(cachedData?.pages as any, propertyTime);
    }
  }, [cachedData, propertyTime, lastTime]);

  const keys = useMemo(() => (
    createKey(baseKey, globalGroupId)
  ), [baseKey, globalGroupId]);

  // the page param is the offset of the page's first record - every page of a fetch
  // filters from the time taken for its first page, as lastTime moves forward with
  // each page received (a moving filter would shift the records under the offset) ...
  const fetchGeofences = useCallback(async({pageParam: resultOffset = 0}) => {
    if (!resultOffset) {
      sinceTimeRef.current = lastTimeRef.current;
    }
    if (sinceTimeRef.current) {
      const results = await loadGeofences({
        ...queryParams,
        resultOffset,
        isFMTracking      : true,
        resultRecordCount : rowsPerPage,
        filterOn          : getFilter(propertyTime, sinceTimeRef.current)
      } as any);
      if (results?.length) {
        // pages are sorted by name, so a later page may hold older updates ...
        const pageTime = getLastTime(results as any, propertyTime);
        setLastTime(previousTime => Math.max(previousTime ?? 0, pageTime));
      }
      return results;
    }
    return [];
  }, [queryParams, rowsPerPage, propertyTime]);

  const query = useInfiniteQuery({
    queryKey         : keys,
    queryFn          : fetchGeofences,
    enabled          : enabled && !!lastTimeRef.current,
    // the feature service caps each response below rowsPerPage, so pages are
    // requested until one comes back empty (see useLoadGeofences) ...
    getNextPageParam : (lastPage, pages) => (
      lastPage?.length ? getLoadedCount(pages) : undefined
    ),
    select : removeTrailingEmptyPage,
    refetchInterval
  } as any);

  const {
    data,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage
  } = query;

  if (enabled && hasNextPage && !isFetchingNextPage) {
    fetchNextPage();
  }

  return createQueryResult(query, {
    results            : data || [],
    isFetchingNextPage : isFetchingNextPage,
    fetchNextPage      : fetchNextPage,
    hasNextPage        : hasNextPage,
    refresh            : refetch,
    enabled
  }) as UseLoadGeofenceResults;
};
